import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IJBPPlanningItem, SMBFractionTypes } from '../../models/IJBPPlanning'
import { OVERALL_CATEGORY } from '../config'
import { TSType } from '../models'

import { getFieldName } from './getFieldName'
import { getIsCollapsedL3Category } from './getIsCollapsedL3Category'

export interface IGetFinSobValueProps {
  targetsItems: IJBPPlanningItem[]
  targetsNoSplitItems: IJBPPlanningItem[]
  actualsItems: IJBPPlanningItem[]
  actualsNoSplitItems: IJBPPlanningItem[]
  quotaItems: IJBPPlanningItem[]
  targetsRollupItems: IJBPPlanningItem[]
  actualsRollupItems: IJBPPlanningItem[]
  actualsPartnerCountItems: IJBPPlanningItem[]

  // SMB vs EU split, only needed where the split rows are rendered
  actualsSmbItems?: IJBPPlanningItem[]
  actualsEuItems?: IJBPPlanningItem[]
  targetsSmbItems?: IJBPPlanningItem[]
  targetsEuItems?: IJBPPlanningItem[]
  smbFraction?: SMBFractionTypes

  formats: IFormActualsTargetsSettings[]
  partnerId: string
  actualsType: ActualsValueTypes
  currency: string
  isCollapsed: boolean
  isShowOnlyCompletedPlans: boolean
  isShowComposedSoB: boolean
  isRecalcPlans: boolean
  isComposedReportMode?: boolean
  isQuarterlyBusinessReviewMode?: boolean

  settingsIndex: number
  type: 'actuals' | 'targets' | 'quota' | 'partnerCount'
  fy: number
  fq: number
  isUnits: boolean
  sType: TSType
  isSoB: boolean
  isNoSplit: boolean
  isRollUp: boolean
  isDirect: boolean // Only for Actuals

  fieldName?: string
}

export const getFinSobValue = ({
  targetsItems,
  targetsNoSplitItems,
  actualsItems,
  actualsNoSplitItems,
  quotaItems,
  targetsRollupItems,
  actualsRollupItems,
  actualsPartnerCountItems,

  actualsSmbItems,
  actualsEuItems,
  targetsSmbItems,
  targetsEuItems,
  smbFraction = SMBFractionTypes.Total,

  formats,
  partnerId,
  actualsType,
  currency,
  isCollapsed,
  isShowOnlyCompletedPlans,
  isShowComposedSoB,
  isRecalcPlans,
  isComposedReportMode,
  isQuarterlyBusinessReviewMode,

  settingsIndex,
  type,
  fy,
  fq,
  isUnits,
  sType,
  isSoB,
  isNoSplit,
  isRollUp,
  isDirect,

  fieldName,
}: IGetFinSobValueProps): number => {
  let result = 0

  // 1. Prepare settings

  if (settingsIndex >= formats.length) {
    return 0
  }

  const setting = formats[settingsIndex]

  // 2. Prepare data source

  let items = !isNoSplit ? targetsItems : targetsNoSplitItems
  if (type === 'actuals') {
    items = !isNoSplit ? actualsItems : actualsNoSplitItems
  }
  if (type === 'quota') {
    items = quotaItems
  }
  if (isRollUp) {
    if (type === 'actuals') {
      items = actualsRollupItems
    } else {
      items = targetsRollupItems
    }
  }
  if (type === 'partnerCount') {
    items = actualsPartnerCountItems
  }
  // SMB vs EU split has its own datasets, only for plain Actuals / Targets
  if (
    smbFraction !== SMBFractionTypes.Total &&
    (type === 'actuals' || type === 'targets') &&
    !isNoSplit &&
    !isRollUp
  ) {
    const isSmb = smbFraction === SMBFractionTypes.SMB
    if (type === 'actuals') {
      items = (isSmb ? actualsSmbItems : actualsEuItems) || []
    } else {
      items = (isSmb ? targetsSmbItems : targetsEuItems) || []
    }
  }

  // 3. Get Value

  let field = fieldName
    ? fieldName
    : getFieldName({ actualsType, isUnits, currency, sType, isSoB, isDirect })

  if (!fieldName && isComposedReportMode) {
    if (isShowOnlyCompletedPlans) {
      field = `${field}Completed`
    }
    if (isShowComposedSoB && !isSoB) {
      field = `${field}Sob`
    }
  }

  const subCat = setting.SubCategory
  const isOverall = subCat === OVERALL_CATEGORY || !subCat
  const l3Cat = setting.L3Category
  const isL3Overall = l3Cat === OVERALL_CATEGORY
  const isL3 = !!l3Cat
  const isL3Collapsed = getIsCollapsedL3Category(items, fy, subCat)

  const isOverallCollapsed = isOverall && isCollapsed
  const isL2Ordinary = !isOverall && !isL3
  const isL3Ordinary = isL3 && !isL3Overall
  const isL3OverallCollapsed = isL3 && isL3Overall && isL3Collapsed

  const isSkipReCalc = isOverallCollapsed || isL2Ordinary || isL3OverallCollapsed || isL3Ordinary

  // Reclculate Target and Actuals
  const isRecalcMode = isComposedReportMode && isRecalcPlans && !isSoB

  if (isRecalcMode && !isSkipReCalc) {
    if (isL3 && isL3Overall && !isL3Collapsed) {
      // Recalc L3 Overall
      const sourceData = items.filter(
        (el) =>
          (el.PartnerId === partnerId || !!isComposedReportMode) &&
          el.ProgramTypes === setting.ProgramType &&
          el.Category === setting.Category &&
          el.SubCategory === setting.SubCategory &&
          // Recalc L3 Overall - sum L3 Details
          el.L3Category &&
          el.L3Category !== OVERALL_CATEGORY &&
          el.FYear === fy &&
          el.FQuarter === fq
      )
      if (sourceData.length >= 1) {
        sourceData.forEach((el) => {
          result += el[field] || 0
        })
      } else {
        // error in data
      }
    } else {
      // Recalc Overall
      const sourceData = items.filter(
        (el) =>
          (el.PartnerId === partnerId || !!isComposedReportMode) &&
          el.ProgramTypes === setting.ProgramType &&
          el.Category === setting.Category &&
          // Summ:
          // 1. Not Overall
          el.SubCategory !== OVERALL_CATEGORY &&
          // 2. L2 not Overall
          (!el.L3Category ||
            // 3. L3 Overall Collapsed
            (el.L3Category &&
              el.L3Category === OVERALL_CATEGORY &&
              getIsCollapsedL3Category(items, fy, el.SubCategory)) ||
            // 4. Recalc L3 Overall Not Collapsed - sum L3 Details
            (el.L3Category &&
              el.L3Category !== OVERALL_CATEGORY &&
              !getIsCollapsedL3Category(items, fy, el.SubCategory))) &&
          //
          el.FYear === fy &&
          el.FQuarter === fq
      )
      if (sourceData.length >= 1) {
        sourceData.forEach((el) => {
          result += el[field] || 0
        })
      } else {
        // error in data
      }
    }
  } else {
    // Items filtered on Top level by: PartnerId, IsActuals, IsTarget, IsQuota
    const sourceData = items.filter(
      (el) =>
        (el.PartnerId === partnerId || !!isComposedReportMode || !!isQuarterlyBusinessReviewMode) &&
        el.ProgramTypes === setting.ProgramType &&
        el.Category === setting.Category &&
        el.SubCategory === setting.SubCategory &&
        el.L3Category === setting.L3Category &&
        el.FYear === fy &&
        el.FQuarter === fq
    )
    if (sourceData.length >= 1) {
      const value = sourceData[0][field]
      result = value === undefined ? null : value
    } else {
      // error in data
    }
  }

  return result
}
